import { descriptionToHtml } from '@mevaser/shared'
import { cn, useToast } from '@mevaser/ui'
import { EditorContent, mergeAttributes, Node, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import {
  Bold,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link2,
  Link2Off,
  List,
  ListOrdered,
} from 'lucide-react'
import * as React from 'react'
import { AiAssistButton, type AiAssistContext } from './ai-assist.js'
import { namePasted } from './attachments.js'

const DESCRIPTION_MAX_LENGTH = 4000
/** Fraction of the cap above which the counter shows — nobody needs to see
 *  "12 / 4000" on a short blurb. Applied to whatever `maxLength` a caller
 *  passes, so a wiki page's much higher cap still only shows near its own
 *  ceiling rather than from the first keystroke. */
const COUNTER_THRESHOLD_RATIO = 0.9

/** A picture inside a paragraph (t1249). Only the editor needs it, so it is
 *  defined here rather than pulling in the full image extension package. */
const InlineImage = Node.create({
  name: 'image',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,
  addAttributes() {
    return { src: { default: null }, alt: { default: null } }
  },
  parseHTML() {
    return [{ tag: 'img[src]' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)]
  },
})

interface RichTextEditorProps {
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  invalid?: boolean
  value: string | null
  onChange: (html: string | null) => void
  /** When present, shows a sparkle button on the toolbar offering AI writing
   *  help for this field (t0763). Silently absent otherwise — existing call
   *  sites need no change. */
  aiAssist?: AiAssistContext
  /** Adds heading (H2/H3) and link toolbar buttons — off by default, so a
   *  product description stays exactly as minimal as before. Wiki pages
   *  (t0896) turn both on. */
  headings?: boolean
  links?: boolean
  /** Overrides the 4000-character product-description cap — a wiki page is a
   *  reference document, not a short blurb. */
  maxLength?: number
  /** When present, pictures can be pasted, dropped or picked into the text
   *  (t1249). Uploads the file and returns the URL to embed. Absent = no
   *  picture support, so product descriptions and mail stay as they were. */
  uploadImage?: (file: File) => Promise<string>
}

/**
 * A minimal WYSIWYG editor: paragraphs, bold, italic, bullet and numbered
 * lists always; headings and links are opt-in (see `headings`/`links`) for
 * longer-form content like wiki pages. `content` only seeds the editor at
 * mount, so callers that need it to pick up a different value later
 * (switching which record is being edited) should remount it with a `key`.
 *
 * `value`/`onChange` carry sanitized-on-save HTML, the same shape stored in
 * the `description` column; a legacy plain-text value is rendered as
 * paragraphs via `descriptionToHtml` so it edits correctly on first open.
 */
export function RichTextEditor({
  id,
  'aria-describedby': describedBy,
  'aria-invalid': ariaInvalid,
  invalid,
  value,
  onChange,
  aiAssist,
  headings = false,
  links = false,
  maxLength = DESCRIPTION_MAX_LENGTH,
  uploadImage,
}: RichTextEditorProps) {
  const toast = useToast()
  const [uploading, setUploading] = React.useState(false)
  const fileInput = React.useRef<HTMLInputElement>(null)
  // The editor is built once, so its handlers read the latest props through refs.
  const uploadRef = React.useRef(uploadImage)
  uploadRef.current = uploadImage
  const editorRef = React.useRef<ReturnType<typeof useEditor>>(null)

  async function insertPictures(pictures: File[]) {
    const upload = uploadRef.current
    if (!upload || pictures.length === 0) return
    setUploading(true)
    try {
      for (const file of pictures) {
        const src = await upload(file)
        editorRef.current
          ?.chain()
          .focus()
          .insertContent({ type: 'image', attrs: { src, alt: '' } })
          .run()
      }
    } catch (error) {
      toast.error('Could not add the picture', error instanceof Error ? error.message : undefined)
    } finally {
      setUploading(false)
    }
  }

  const pictureFiles = (list: FileList | null | undefined) =>
    Array.from(list ?? []).filter((f) => f.type.startsWith('image/'))

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: headings ? { levels: [2, 3] } : false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        link: links ? { openOnClick: false } : false,
        underline: false,
      }),
      ...(uploadImage ? [InlineImage] : []),
    ],
    content: value ? descriptionToHtml(value) : '',
    editorProps: {
      attributes: {
        id: id ?? '',
        class: 'rich-text min-h-20 px-2.5 py-1.5 text-sm focus:outline-none',
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(ariaInvalid ? { 'aria-invalid': 'true' } : {}),
      },
      handlePaste: (_view, event) => {
        const pictures = pictureFiles(event.clipboardData?.files)
        if (!uploadRef.current || pictures.length === 0) return false
        event.preventDefault()
        void insertPictures(namePasted(pictures))
        return true
      },
      handleDrop: (_view, event) => {
        const pictures = pictureFiles(event.dataTransfer?.files)
        if (!uploadRef.current || pictures.length === 0) return false
        event.preventDefault()
        void insertPictures(namePasted(pictures))
        return true
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? null : e.getHTML()),
  })

  editorRef.current = editor

  // v3 disables per-transaction rerenders by default, so toolbar active
  // states have to be read through useEditorState rather than calling
  // editor.isActive() directly during render.
  const state = useEditorState({
    editor,
    selector: (ctx) => ({
      bold: ctx.editor?.isActive('bold') ?? false,
      italic: ctx.editor?.isActive('italic') ?? false,
      bulletList: ctx.editor?.isActive('bulletList') ?? false,
      orderedList: ctx.editor?.isActive('orderedList') ?? false,
      heading2: ctx.editor?.isActive('heading', { level: 2 }) ?? false,
      heading3: ctx.editor?.isActive('heading', { level: 3 }) ?? false,
      link: ctx.editor?.isActive('link') ?? false,
    }),
  })

  const length = value?.length ?? 0
  const counterThreshold = Math.round(maxLength * COUNTER_THRESHOLD_RATIO)

  const toggleLink = () => {
    if (!editor) return
    if (state?.link) {
      editor.chain().focus().unsetLink().run()
      return
    }
    const url = window.prompt('Link URL')
    if (!url) return
    editor.chain().focus().setLink({ href: url }).run()
  }

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-surface text-sm transition-colors',
        'focus-within:border-primary',
        invalid && 'border-danger',
      )}
    >
      <div className="flex items-center gap-1 border-b border-border px-1.5 py-1">
        <ToolbarButton
          label="Bold"
          active={state?.bold}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={state?.italic}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Bullet list"
          active={state?.bulletList}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List className="size-3.5" />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={state?.orderedList}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-3.5" />
        </ToolbarButton>
        {headings ? (
          <>
            <ToolbarButton
              label="Heading"
              active={state?.heading2}
              onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            >
              <Heading2 className="size-3.5" />
            </ToolbarButton>
            <ToolbarButton
              label="Subheading"
              active={state?.heading3}
              onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            >
              <Heading3 className="size-3.5" />
            </ToolbarButton>
          </>
        ) : null}
        {links ? (
          <ToolbarButton
            label={state?.link ? 'Remove link' : 'Add link'}
            active={state?.link}
            onClick={toggleLink}
          >
            {state?.link ? <Link2Off className="size-3.5" /> : <Link2 className="size-3.5" />}
          </ToolbarButton>
        ) : null}
        {uploadImage ? (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              className="hidden"
              onChange={(e) => {
                void insertPictures(pictureFiles(e.target.files))
                e.target.value = ''
              }}
            />
            <ToolbarButton
              label={uploading ? 'Adding picture…' : 'Insert picture'}
              onClick={() => fileInput.current?.click()}
            >
              <ImagePlus className="size-3.5" />
            </ToolbarButton>
          </>
        ) : null}
        {aiAssist ? (
          <div className="ms-auto flex items-center gap-1">
            <AiAssistButton
              target="rich-text"
              value={value}
              onApply={(html) => {
                editor?.commands.setContent(html)
                onChange(html)
              }}
              context={aiAssist}
            />
          </div>
        ) : null}
        {length > counterThreshold ? (
          <span
            className={cn(
              'text-2xs tabular',
              aiAssist ? 'ms-2' : 'ms-auto',
              length > maxLength ? 'text-danger' : 'text-foreground-muted',
            )}
          >
            {length} / {maxLength}
          </span>
        ) : null}
      </div>
      <EditorContent editor={editor} />
    </div>
  )
}

function ToolbarButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string
  active?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      // Keeps the current text selection — a plain click would blur the
      // editor first and toggling bold would apply to nothing.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'flex size-6 items-center justify-center rounded text-foreground-muted transition-colors',
        'hover:bg-surface-sunken hover:text-foreground',
        active && 'bg-surface-sunken text-foreground',
      )}
    >
      {children}
    </button>
  )
}
